import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineBusIcon from 'LuxKit/Icons/line/LineBusIcon'
import LineCameraIcon from 'LuxKit/Icons/line/LineCameraIcon'
import LineCoffeeIcon from 'LuxKit/Icons/line/LineCoffeeIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineTreesIcon from 'LuxKit/Icons/line/LineTreesIcon'
import LineUtensilsIcon from 'LuxKit/Icons/line/LineUtensilsIcon'
import type { TripListRow, TripListRowType } from './tripListTypes'

const TypeIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(28)};
  height: ${rem(28)};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  flex-shrink: 0;

  &.type-flight {
    background: ${props => props.theme.palette.product.flights.seats.upfront.lightBackground};
    color: ${props => props.theme.palette.product.flights.seats.upfront.normalForeground};
  }

  &.type-accommodation,
  &.type-experience {
    background: ${props => props.theme.palette.highlight.tertiary.lightBackground};
    color: ${props => props.theme.palette.highlight.tertiary.normalForeground};
  }

  &.type-dining {
    background: ${props => props.theme.palette.messaging.warning.lightBackground};
    color: ${props => props.theme.palette.messaging.warning.normalForeground};
  }

  &.type-activity {
    background: ${props => props.theme.palette.messaging.success.lightBackground};
    color: ${props => props.theme.palette.messaging.success.normalForeground};
  }

  &.type-transport,
  &.type-cruise {
    background: ${props => props.theme.palette.highlight.primary.lightBackground};
    color: ${props => props.theme.palette.highlight.primary.normalForeground};
  }
`

function getActivityIcon(row: TripListRow | undefined): React.ReactNode {
  const category = `${row?.subtitle ?? ''} ${row?.typeLabel ?? ''}`.toLowerCase()

  if (category.includes('cafe') || category.includes('coffee')) {
    return <LineCoffeeIcon size="S" />
  }

  if (category.includes('park') || category.includes('garden')) {
    return <LineTreesIcon size="S" />
  }

  return <LineCameraIcon size="S" />
}

function getTypeIcon(type: TripListRowType, row: TripListRow | undefined): React.ReactNode {
  switch (type) {
    case 'accommodation':
      return <LineBedIcon size="S" />
    case 'experience':
      return <LineTicketIcon size="S" />
    case 'dining':
      return <LineUtensilsIcon size="S" />
    case 'flight':
      return <LinePlaneFlyIcon size="S" />
    case 'transport':
      return row ? <LineBusIcon size="S" /> : <LineMapMarkerIcon size="S" />
    case 'cruise':
      return <LineShipIcon size="S" />
    case 'activity':
      return getActivityIcon(row)
    default:
      return null
  }
}

interface Props {
  type: TripListRowType
  row?: TripListRow
}

function TripListTypeIcon({ type, row }: Props) {
  return (
    <TypeIcon className={`type-${type}`}>
      {getTypeIcon(type, row)}
    </TypeIcon>
  )
}

export default TripListTypeIcon
